"use client";

import {
  Building2,
  Heart,
  LayoutGrid,
  Lock,
  ShieldUser,
  Star,
  Sun,
  Users,
} from "lucide-react";
import TrackedSection from "./TrackedSection";
import { CategoryCard } from "./CategoryCard";

type IconVariant =
  | "hero"
  | "info"
  | "success"
  | "warning"
  | "chart2"
  | "chart3"
  | "chart4"
  | "destructive";

interface Category {
  name: string;
  href: string;
  description: string;
  ctaText: string;
  icon: React.ReactNode;
  iconVariant: IconVariant;
}

interface CustomDressCategoriesSectionProps {
  title?: string;
  subtitle?: string;
  categories?: Category[];
  className?: string;
}

const DEFAULT_CATEGORIES: Category[] = [
  {
    name: "Wedding Dresses",
    href: "/custom-wedding-dresses",
    description:
      "Design your wedding dress with AI, review a price estimate, and have the approved design made to your measurements. Choose the neckline, silhouette, sleeves, and details for your celebration.",
    ctaText: "Design Your Wedding Dress",
    icon: <Heart className="w-8 h-8" aria-hidden />,
    iconVariant: "hero",
  },
  {
    name: "Modest Dresses",
    href: "/custom-modest-dresses",
    description:
      "Choose the coverage you want for a modest wedding dress or formal gown, from high necklines to long sleeves and floor-length hems. Made to your measurements with premium fabrics.",
    ctaText: "Design Your Modest Dress",
    icon: <Lock className="w-8 h-8" aria-hidden />,
    iconVariant: "info",
  },
  {
    name: "Hijabi Wedding Dresses",
    href: "/custom-hijabi-wedding-dresses",
    description:
      "Wedding and formal dresses for hijab-wearing women, with high necklines, long sleeves, and floor-length hems. Made to your measurements with premium fabrics and full coverage.",
    ctaText: "Design Your Hijabi Dress",
    icon: <ShieldUser className="w-8 h-8" aria-hidden />,
    iconVariant: "success",
  },
  {
    name: "Prom Dresses",
    href: "/custom-prom-dresses",
    description:
      "Create a prom dress that reflects your style, made to your measurements by a professional dressmaker.",
    ctaText: "Design Your Prom Dress",
    icon: <Star className="w-8 h-8" aria-hidden />,
    iconVariant: "warning",
  },
  {
    name: "Quinceanera Dresses",
    href: "/custom-quinceanera-dresses",
    description:
      "Celebrate your special day with a custom quinceanera dress that reflects your unique style and personality.",
    ctaText: "Design Your Quinceanera Dress",
    icon: <Sun className="w-8 h-8" aria-hidden />,
    iconVariant: "chart4",
  },
  {
    name: "Bridesmaid Dresses",
    href: "/custom-bridesmaid-dresses",
    description:
      "Create coordinated bridesmaid dresses to match your wedding theme and suit each person in your party.",
    ctaText: "Design Bridesmaid Dresses",
    icon: <Users className="w-8 h-8" aria-hidden />,
    iconVariant: "chart2",
  },
  {
    name: "Formal Dresses",
    href: "/custom-formal-dresses",
    description:
      "Design a custom evening gown for your next formal event, tailored to your style and measurements.",
    ctaText: "Design Your Formal Dress",
    icon: <Building2 className="w-8 h-8" aria-hidden />,
    iconVariant: "chart3",
  },
  {
    name: "View All Designs",
    href: "/gallery",
    description:
      "Explore our complete gallery of custom dress designs and get inspired for your next special occasion.",
    ctaText: "Browse All Designs",
    icon: <LayoutGrid className="w-8 h-8" aria-hidden />,
    iconVariant: "destructive",
  },
];

export default function CustomDressCategoriesSection({
  title = "Explore Our Custom Dress Categories",
  subtitle = "Explore wedding dresses, prom dresses, and formal gowns to find a starting point for your design.",
  categories = DEFAULT_CATEGORIES,
  className = "",
}: CustomDressCategoriesSectionProps) {
  return (
    <TrackedSection
      sectionId="custom-dress-categories"
      sectionName="Custom Dress Categories"
      className={`bg-background py-16 sm:py-24 ${className}`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            {title}
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
            {subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category, index) => (
            <CategoryCard
              key={index}
              href={category.href}
              iconVariant={category.iconVariant}
              icon={category.icon}
              title={category.name}
              description={category.description}
              ctaText={category.ctaText}
            />
          ))}
        </div>
      </div>
    </TrackedSection>
  );
}
